import {Logic, ReelConstants, LogicEvents, Timer} from '@gongxigames/framework/slot';
import {GameClientStub} from 'com/gongxigames/math/core/client/GameClientStub';
import {GameClientProxy} from 'com/gongxigames/math/core/client/GameClientProxy';
import {Bonus} from 'com/gongxigames/math/core/game/engine/bonus/Bonus';
import {WolfHavenClient} from 'com/gongxigames/math/wolfhaven/client/WolfHavenClient';
import {WolfHavenEngine} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenEngine';
import {WolfHavenSession} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenSession';
import {WolfHavenSpin} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenSpin';
import {ReelSet} from 'com/gongxigames/math/core/game/ReelSet';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {WolfHaven} from 'com/gongxigames/math/wolfhaven/engine/WolfHaven';
import {WolfHavenFreeSpinEngine} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenFreeSpinEngine';
import {Pay} from 'com/gongxigames/math/core/game/Pay';
import Position = Pay.Position;
import {WolfHavenFreeGamesBonus} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenFreeGamesBonus';
import {GameHistoryEntry} from 'com/gongxigames/math/core/client/history/GameHistoryEntry';
import {Spin} from 'com/gongxigames/math/core/game/Spin';
import {WolfHavenPickBonus} from 'com/gongxigames/math/wolfhaven/engine/WolfHavenPickBonus';

export class GameLogic extends Logic<WolfHavenSession, WolfHavenEngine> {
    protected createProxy(): GameClientProxy<WolfHavenSession, WolfHavenEngine> {
        return WolfHavenClient.createProxy();
    }

    protected createdStub(): GameClientStub<WolfHavenSession, WolfHavenEngine> {
        const data = this.getSessionFromLocalStorage()!;

        return WolfHavenClient.createStub(data);
    }

    public getReelsBeforeFS(spin: WolfHavenSpin): ReelSet {
        return WolfHavenEngine.createMainGameReelSet(spin);
    }

    public getReelSet(): ReelSet {
        if (this.previousAction === Action.SPIN) {
            return WolfHavenEngine.createMainGameReelSet(this.spinData as WolfHavenSpin);
        } else if (this.previousAction === Action.FREESPIN) {
            return WolfHavenFreeSpinEngine.createBonusReelSet(this.spinData as WolfHavenSpin);
        }

        return super.getReelSet();
    }

    public getNextReelSet(): ReelSet {
        if (this.nextAction === Action.SPIN) {
            if (!this.spinData) {
                return WolfHaven.BG_SINGLE_REEL_SET;
            }

            return WolfHavenEngine.createMainGameReelSet(this.spinData as WolfHavenSpin);
        } else if (this.nextAction === Action.FREESPIN) {
            if (!this.spinData) {
                return WolfHaven.BG_SINGLE_REEL_SET;
            }

            return WolfHavenFreeSpinEngine.createBonusReelSet(this.spinData as WolfHavenSpin);
        }

        return null!;
    }

    public getPositionsToFade(): Position[] {
        return (this.spinData as WolfHavenSpin).getNoWinSymbolsPositions();
    }

    public getRandomWildPositions(): Position[] {
        return (this.spinData as WolfHavenSpin).getWildsAward();
    }

    public isActivatorOnReels(): boolean {
        const spin = (this.spinData as WolfHavenSpin);

        return spin.symbolIds[ReelConstants.REELS_AMOUNT - 1].indexOf(WolfHaven.Activator) !== -1;
    }

    public isRandomWildFeature(): boolean {
        return (this._client.getEngine() as WolfHavenEngine).hasActivator(this.spinData as WolfHavenSpin);
    }

    public getWolfSession(): WolfHavenSession {
        return this._client.getSession() as WolfHavenSession;
    }

    public getFreeGamesType(): number {
        return this.getFreeGames().getFreeGameType();
    }

    public getFreeGames(): WolfHavenFreeGamesBonus {
        return this._client.getSession().getBonus(Bonus.FREE_GAMES_BONUS) as WolfHavenFreeGamesBonus;
    }

    public getWolfPickBonus(): WolfHavenPickBonus {
        return this._client.getSession().getBonus(Bonus.PICK_BONUS) as WolfHavenPickBonus;
    }

    public getFinalSpinStopPositions(): number[] {
        return this._client.getSession().getRandomValuesList().getUsedValues();
    }

    public getClientSession(): any {
        return this._client.getSession();
    }

    protected getRestoredSpinData(): Spin {
        const actionNumber = this.getActionNumberForLastSpin();
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(actionNumber);

        if (historyEntry.getSpin(this._client.getEngine())) {
            return historyEntry.getSpin(this._client.getEngine());
        }

        if (this.getFreeGames() && this.getFreeGames().isBuyBonus()) {
            return null!;
        }

        return this.getSessionForPickBonusWinningRound(Bonus.FREE_GAMES_BONUS);
    }

    public getSessionForPickBonusWinningRound(bonusId: number): Spin {
        const actionNumber = this.getPickBonusGameActionNumber(bonusId);

        if (actionNumber === null) {
            return null!;
        }
        const historyEntry: GameHistoryEntry = this._client.getGameHistory().getEntry(actionNumber);

        return historyEntry.getSpin(this._client.getEngine());
    }

    protected getPickBonusGameActionNumber(bonusId: number): number {
        const bonus = this.getCurrentBonus(bonusId);

        if (!bonus) {
            return null!;
        }
        const currentActionNumber = bonus.getActionNumber();
        const engine = this._client.getEngine();
        const spinEntry = this._client.getGameHistory().getEntry(currentActionNumber);
        const pickBonusActionNumber = spinEntry.getSessionAfter(engine).getBonus(Bonus.PICK_BONUS).getActionNumber();

        return pickBonusActionNumber;
    }

    public isRetriggerWon(): boolean {
        return this._spin.action === Action.FREESPIN && this.getRetriggerCount() !== undefined && this.getRetriggerCount() > 0;
    }

    public freespin(cb: Function): void {
        this._callback = cb;
        this._updateManager.add(new Timer(() => {
            this.previousSpinRoundWin = this.isFirstFreeSpinOfBuyBonus() ? 0 : this.getTotalWin();
            this.previousAction = this.nextAction;
            this._retriggerCount = this.getFreeGamesTotalCount();
            this._eventManager.emit(LogicEvents.ROUND_START, 0);
            this.emitRoundStarted();
            this._clientWrapper.freeSpin();
        }, 0, true));
    }

    public isFirstFreeSpinOfBuyBonus(): boolean {
        return this.getFreeGames() !== null && this.getFreeGames().isBuyBonus()
            && this.getFreeGames().getIndex() === 0;
    }
}
